import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = 'https://cook-ai-backend-production.up.railway.app/v1';
const APP_STORE_URL = 'https://apps.apple.com/us/app/cook-ai-hey-chef/id6758898666';
const APP_SCHEME = 'thecookai';

// Brand colors from the mobile app
const COLORS = {
    primaryBlue: '#0598CE',
    darkBlue: '#113768',
    accent: '#007AFF',
    gradientStart: '#007AFF',
    gradientEnd: '#3395FF',
    white: '#FFFFFF',
    whiteAlpha30: 'rgba(255,255,255,0.30)',
    whiteAlpha15: 'rgba(255,255,255,0.15)',
    whiteAlpha10: 'rgba(255,255,255,0.10)',
    blackAlpha30: 'rgba(0,0,0,0.30)',
    blackAlpha50: 'rgba(0,0,0,0.50)',
    blackAlpha70: 'rgba(0,0,0,0.70)',
    blackAlpha90: 'rgba(0,0,0,0.90)',
    youtube: '#FF0000',
};

interface DishData {
    id: string;
    dishName: string;
    cuisineType: string;
    pictureUrl: string;
    estimatedCalories: number;
    estimatedOutsideCost: number;
    estimatedHomeCost: number;
    shortDescription: string;
    steps: string[];
    estimatedTime?: string;
    difficulty?: string;
    videoUrl?: string;
    ingredients: string[];
}

const difficultyColor = (d?: string) => {
    if (!d) return COLORS.whiteAlpha30;
    const l = d.toLowerCase();
    if (l === 'easy') return '#4CAF50';
    if (l === 'hard') return '#FF4C4C';
    return '#FF9800';
};

export const DishPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [dish, setDish] = useState<DishData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE}/recipes/share/${id}`)
            .then(r => r.json())
            .then(json => {
                if (json.success && json.data) setDish(json.data);
                else setError(true);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (!dish) return;
        document.title = `${dish.dishName} — Cook AI`;
        const set = (prop: string, content: string) => {
            let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement;
            if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
            el.setAttribute('content', content);
        };
        set('og:title', `${dish.dishName} — Cook AI`);
        set('og:description', dish.shortDescription);
        if (dish.pictureUrl) set('og:image', dish.pictureUrl);
        set('og:url', `https://thecookai.app/dish/${dish.id}`);
    }, [dish]);

    const handleOpenInApp = () => {
        window.location.href = `${APP_SCHEME}://dish/${id}`;
        setTimeout(() => { window.location.href = APP_STORE_URL; }, 2000);
    };

    const handleWatchYoutube = () => {
        if (dish?.videoUrl) window.open(dish.videoUrl, '_blank', 'noopener');
    };

    return (
        <div style={{ minHeight: '100vh', background: '#0a0a0a', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: COLORS.white }}>

            {/* ── Sticky Nav ── */}
            <nav style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                background: scrolled ? 'rgba(17,55,104,0.95)' : 'transparent',
                backdropFilter: scrolled ? 'blur(12px)' : 'none',
                transition: 'background 0.3s ease',
                padding: '14px 20px',
                display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : 'none',
            }}>
                <img src="https://thecookai.app/images/cook_ai_logo.webp" alt="Cook AI" style={{ width: 32, height: 32, borderRadius: 8 }} />
                <span style={{ fontWeight: 700, fontSize: 17, color: COLORS.white }}>Cook AI</span>
            </nav>

            {/* ── Hero: full-screen image with dark gradient ── */}
            {loading && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: 16 }}>
                    <div style={{ width: 40, height: 40, border: `3px solid ${COLORS.primaryBlue}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>Loading recipe...</span>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}

            {error && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 12, padding: 24, textAlign: 'center' }}>
                    <div style={{ fontSize: 64 }}>🍽️</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>Recipe not found</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>This recipe may have been removed or the link is invalid.</div>
                    <a href="/" style={{ color: COLORS.primaryBlue, textDecoration: 'none', fontWeight: 600, marginTop: 8 }}>Go to Cook AI →</a>
                </div>
            )}

            {dish && (
                <>
                    {/* Hero image block */}
                    <div style={{
                        position: 'relative',
                        height: '65vh',
                        minHeight: 380,
                        maxHeight: 600,
                        overflow: 'hidden',
                    }}>
                        {dish.pictureUrl && (
                            <img
                                src={dish.pictureUrl}
                                alt={dish.dishName}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        )}
                        {/* Dark gradient overlay — matches mobile exactly */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.95) 100%)',
                        }} />

                        {/* Hero content */}
                        <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            padding: '0 20px 28px',
                            maxWidth: 680, margin: '0 auto',
                        }}>
                            {/* Culture tag */}
                            {dish.cuisineType && (
                                <div style={{
                                    display: 'inline-block',
                                    background: COLORS.primaryBlue,
                                    color: COLORS.white,
                                    fontSize: 13, fontWeight: 600,
                                    padding: '6px 16px', borderRadius: 20,
                                    marginBottom: 12,
                                }}>
                                    {dish.cuisineType}
                                </div>
                            )}

                            {/* Dish name */}
                            <h1 style={{
                                fontSize: 'clamp(26px, 5vw, 36px)',
                                fontWeight: 800,
                                color: COLORS.white,
                                margin: '0 0 14px',
                                textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                                lineHeight: 1.15,
                            }}>
                                {dish.dishName}
                            </h1>

                            {/* Info row: time · difficulty · calories */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: 10 }}>
                                {dish.estimatedTime && (
                                    <span style={{ color: COLORS.white, fontSize: 14, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                        ⏱ {dish.estimatedTime}
                                    </span>
                                )}
                                {dish.difficulty && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: COLORS.white, fontSize: 14, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: difficultyColor(dish.difficulty), display: 'inline-block' }} />
                                        {dish.difficulty}
                                    </span>
                                )}
                                {dish.estimatedCalories > 0 && (
                                    <span style={{ color: COLORS.white, fontSize: 14, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                        🔥 {dish.estimatedCalories} cal
                                    </span>
                                )}
                                {dish.estimatedHomeCost > 0 && (
                                    <span style={{ color: COLORS.white, fontSize: 14, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                        🏠 ${dish.estimatedHomeCost} home
                                    </span>
                                )}
                                {dish.estimatedOutsideCost > 0 && (
                                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                        🏪 ${dish.estimatedOutsideCost} outside
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Content area ── */}
                    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 20px 160px' }}>

                        {/* Description */}
                        {dish.shortDescription && (
                            <div style={{
                                background: COLORS.blackAlpha30,
                                borderRadius: 12,
                                padding: '14px 16px',
                                borderLeft: `3px solid rgba(255,255,255,0.2)`,
                                marginBottom: 28,
                            }}>
                                <p style={{ color: COLORS.white, fontSize: 15, lineHeight: 1.65, margin: 0, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                    {dish.shortDescription}
                                </p>
                            </div>
                        )}

                        {/* Ingredients */}
                        {dish.ingredients && dish.ingredients.length > 0 && (
                            <section style={{ marginBottom: 30 }}>
                                <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.white, marginBottom: 14, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                    🥄 Ingredients <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>({dish.ingredients.length})</span>
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {dish.ingredients.map((item, i) => {
                                        const match = item.match(/^([\d./]+\s*[a-zA-Z]*)\s+(.+)$/);
                                        const amount = match?.[1]?.trim() ?? '';
                                        const name = match?.[2]?.trim() ?? item;
                                        return (
                                            <div key={i} style={{
                                                display: 'flex', alignItems: 'center', gap: 14,
                                                background: COLORS.whiteAlpha10,
                                                borderRadius: 16, padding: '14px 16px',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                            }}>
                                                <span style={{ fontSize: 24 }}>🥄</span>
                                                <div>
                                                    <div style={{ color: COLORS.white, fontSize: 15, fontWeight: 600 }}>{name}</div>
                                                    {amount && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 2 }}>{amount}</div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        {/* Steps */}
                        {dish.steps && dish.steps.length > 0 && (
                            <section style={{ marginBottom: 30 }}>
                                <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.white, marginBottom: 14, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                    📋 Instructions
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {dish.steps.map((step, i) => (
                                        <div key={i} style={{
                                            display: 'flex', gap: 14, alignItems: 'flex-start',
                                            background: COLORS.blackAlpha30,
                                            borderRadius: 12, padding: 14,
                                            borderLeft: '3px solid rgba(255,255,255,0.25)',
                                        }}>
                                            <div style={{
                                                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                                                background: COLORS.whiteAlpha15,
                                                border: '2px solid rgba(255,255,255,0.28)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 13, fontWeight: 700, color: COLORS.white,
                                            }}>
                                                {i + 1}
                                            </div>
                                            <p style={{ color: COLORS.white, fontSize: 14, lineHeight: 1.65, margin: '6px 0 0', textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                                {step}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* ── Sticky footer ── */}
                    <div style={{
                        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
                        background: 'rgba(10,10,10,0.96)',
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(16px)',
                        padding: '12px 20px',
                        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                    }}>
                        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {/* Open in App — primary gradient button like mobile */}
                            <button onClick={handleOpenInApp} style={{
                                width: '100%', padding: '15px',
                                borderRadius: 25, border: 'none', cursor: 'pointer',
                                background: `linear-gradient(135deg, ${COLORS.gradientStart} 0%, ${COLORS.gradientEnd} 100%)`,
                                color: COLORS.white, fontSize: 16, fontWeight: 700,
                                boxShadow: '0 4px 20px rgba(0,122,255,0.4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            }}>
                                <img src="https://thecookai.app/images/cook_ai_logo.webp" alt="" style={{ width: 22, height: 22, borderRadius: 5 }} />
                                Open in Cook AI App
                            </button>

                            {/* Bottom row: Watch Tutorial + Download */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                {dish.videoUrl && (
                                    <button onClick={handleWatchYoutube} style={{
                                        flex: 1, padding: '13px 10px',
                                        borderRadius: 20, cursor: 'pointer',
                                        background: COLORS.whiteAlpha10,
                                        border: '1px solid rgba(255,0,0,0.4)',
                                        color: COLORS.white, fontSize: 14, fontWeight: 600,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                    }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill={COLORS.youtube}>
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                        </svg>
                                        Watch Tutorial
                                    </button>
                                )}
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{
                                    flex: 1, padding: '13px 10px',
                                    borderRadius: 20,
                                    background: COLORS.whiteAlpha10,
                                    border: `1px solid ${COLORS.whiteAlpha30}`,
                                    color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600,
                                    textDecoration: 'none',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                }}>
                                    ↓ App Store
                                </a>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
